import qs from "qs"

import { TslNewsletterWorkerClient } from "./TslNewsletterWorkerClient"
import type {
  TslNewsletterArticle,
  TslNewsletterArticlesQuery,
  TslNewsletterEvent,
  TslNewsletterEventsQuery,
  TslNewsletterListResponse,
  TslNewsletterPreviewJson,
  TslNewsletterPreviewQuery,
  TslNewsletterSource,
  TslNewsletterSourceCreatePayload,
  TslNewsletterSourcesQuery,
  TslNewsletterSubscriber,
  TslNewsletterSubscribersQuery,
} from "./types/tslNewsletter"

const buildQuery = (query?: Record<string, unknown>) => {
  if (!query) return ""
  const cleaned: Record<string, unknown> = {}
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return
    cleaned[k] = v
  })
  const str = qs.stringify(cleaned, { arrayFormat: "repeat" })
  return str ? `?${str}` : ""
}

const listSources = (query?: TslNewsletterSourcesQuery) =>
  TslNewsletterWorkerClient.get<undefined, TslNewsletterListResponse<TslNewsletterSource>>(
    `/tsl-newsletter/sources${buildQuery(query)}`,
  )

const createSource = (payload: TslNewsletterSourceCreatePayload) =>
  TslNewsletterWorkerClient.post<undefined, TslNewsletterSource>(`/tsl-newsletter/sources`, payload)

const deleteSource = (id: string) =>
  TslNewsletterWorkerClient.delete<undefined, void>(
    `/tsl-newsletter/sources/${encodeURIComponent(id)}`,
  )

const listArticles = (query?: TslNewsletterArticlesQuery) =>
  TslNewsletterWorkerClient.get<undefined, TslNewsletterListResponse<TslNewsletterArticle>>(
    `/tsl-newsletter/articles${buildQuery(query)}`,
  )

const getArticle = (id: string) =>
  TslNewsletterWorkerClient.get<undefined, TslNewsletterArticle>(
    `/tsl-newsletter/articles/${encodeURIComponent(id)}`,
  )

const reprocessArticle = (id: string) =>
  TslNewsletterWorkerClient.post<undefined, TslNewsletterArticle>(
    `/tsl-newsletter/articles/${encodeURIComponent(id)}/reprocess`,
  )

const deleteArticle = (id: string) =>
  TslNewsletterWorkerClient.delete<undefined, void>(
    `/tsl-newsletter/articles/${encodeURIComponent(id)}`,
  )

const listSubscribers = (query?: TslNewsletterSubscribersQuery) =>
  TslNewsletterWorkerClient.get<undefined, TslNewsletterListResponse<TslNewsletterSubscriber>>(
    `/tsl-newsletter/subscribers${buildQuery(query)}`,
  )

const getSubscriber = (id: string) =>
  TslNewsletterWorkerClient.get<undefined, TslNewsletterSubscriber>(
    `/tsl-newsletter/subscribers/${encodeURIComponent(id)}`,
  )

const deleteSubscriber = (id: string) =>
  TslNewsletterWorkerClient.delete<undefined, void>(
    `/tsl-newsletter/subscribers/${encodeURIComponent(id)}`,
  )

const listEvents = (query?: TslNewsletterEventsQuery) =>
  TslNewsletterWorkerClient.get<undefined, TslNewsletterListResponse<TslNewsletterEvent>>(
    `/tsl-newsletter/events${buildQuery(query)}`,
  )

const previewNewsletterHtml = (query: TslNewsletterPreviewQuery) =>
  TslNewsletterWorkerClient.get<undefined, string>(
    `/tsl-newsletter/preview${buildQuery({ ...query, format: "html" })}`,
    { responseType: "text", transformResponse: [(data: unknown) => data] },
  )

const previewNewsletterJson = (query: TslNewsletterPreviewQuery) =>
  TslNewsletterWorkerClient.get<undefined, TslNewsletterPreviewJson>(
    `/tsl-newsletter/preview${buildQuery({ ...query, format: "json" })}`,
  )

export const TslNewsletterService = {
  listSources,
  createSource,
  deleteSource,
  listArticles,
  getArticle,
  reprocessArticle,
  deleteArticle,
  listSubscribers,
  getSubscriber,
  deleteSubscriber,
  listEvents,
  previewNewsletterHtml,
  previewNewsletterJson,
}
